// Interface variants (R-U8): where the choice is not settled, a switcher at the bottom lets the owner compare; choices live in this browser.

import { useSyncExternalStore } from 'react';

export const VARIANTS = {
  theme: { label: 'Тема', options: [['system', 'Как в системе'], ['light', 'Светлая'], ['dark', 'Тёмная']] },
  font: { label: 'Шрифт', options: [['inter', 'Inter'], ['onest', 'Onest'], ['golos', 'Golos']] },
  // The numbers are the row heights the variant really gives (--row-h 40 and 48 in styles.css), not wishes (M3).
  density: { label: 'Плотность', options: [['compact', 'Плотно · 40'], ['comfortable', 'Свободно · 48']] },
  radius: { label: 'Углы', options: [['soft', 'Мягкие · 6'], ['sharp', 'Строгие · 3']] },
  canvas: { label: 'Фон таблицы', options: [['surface', 'Белый'], ['sunken', 'Темнее страницы']] },
} as const;
export type VariantName = keyof typeof VARIANTS;

const KEY = 'confdeck:variants';
const listeners = new Set<() => void>();
function read(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, string>; } catch { return {}; }
}
let state = typeof window === 'undefined' ? {} : read();

export function getVariant(name: VariantName): string { return state[name] ?? VARIANTS[name].options[0][0]; }

export function setVariant(name: VariantName, value: string) {
  state = { ...state, [name]: value };
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* per-browser convenience only */ }
  apply();
  for (const l of listeners) l();
}

const dark = typeof window !== 'undefined' ? matchMedia('(prefers-color-scheme: dark)') : null;
/** Writes variants to <html> data attributes; the shared tokens follow them. */
export function apply() {
  const el = document.documentElement;
  const theme = getVariant('theme');
  const next = theme === 'system' ? (dark?.matches ? 'dark' : 'light') : theme;
  // A theme change paints in one frame: transitions are off until the next two frames (DESIGN.md «Движение»).
  if (el.dataset.theme && el.dataset.theme !== next) { el.dataset.switching = ''; requestAnimationFrame(() => requestAnimationFrame(() => { delete el.dataset.switching; })); }
  el.dataset.theme = next;
  el.dataset.font = getVariant('font');
  el.dataset.density = getVariant('density');
  el.dataset.radius = getVariant('radius');
  el.dataset.canvas = getVariant('canvas');
}
dark?.addEventListener('change', apply);

export function useVariant(name: VariantName): [string, (v: string) => void] {
  const v = useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => getVariant(name));
  return [v, (nv) => setVariant(name, nv)];
}
